import * as React from 'react';
import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { BlockRule } from '@/lib/api/blockRules';

export interface BlockRuleFormValues {
    name: string;
    operator: 'OR' | 'AND';
    ipAddress: string;
    email: string;
    emailRegex: string;
    bin: string;
    binRegex: string;
    isPermanent: boolean;
    ttlHours: string;
}

interface BlockRuleFormProps {
    blockRule?: BlockRule;
    onSubmit: (values: BlockRuleFormValues) => Promise<void>;
    isSubmitting: boolean;
}

function validate(
    values: BlockRuleFormValues,
    hasStoredExpiry: boolean,
): Partial<Record<keyof BlockRuleFormValues, string>> {
    const errors: Partial<Record<keyof BlockRuleFormValues, string>> = {};
    if (!values.name.trim()) errors.name = 'Name is required';
    if (values.ipAddress && !/^(\d{1,3}\.){3}\d{1,3}$|^[0-9a-fA-F:]+$/.test(values.ipAddress)) {
        errors.ipAddress = 'Invalid IP address format';
    }
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = 'Invalid email format';
    }
    if (values.ttlHours) {
        const h = parseInt(values.ttlHours, 10);
        if (isNaN(h) || h < 1 || h > 8760) errors.ttlHours = 'Must be between 1 and 8760 hours';
    } else if (!values.isPermanent && !hasStoredExpiry) {
        errors.ttlHours = 'Set an expiry or make the rule permanent, otherwise it never blocks anything';
    }
    return errors;
}

export function BlockRuleForm({ blockRule, onSubmit, isSubmitting }: BlockRuleFormProps) {
    const [values, setValues] = React.useState<BlockRuleFormValues>({
        name: blockRule?.name ?? '',
        operator: (blockRule?.operator as 'OR' | 'AND') ?? 'OR',
        ipAddress: blockRule?.ipAddress ?? '',
        email: blockRule?.email ?? '',
        emailRegex: blockRule?.emailRegex ?? '',
        bin: blockRule?.bin ?? '',
        binRegex: blockRule?.binRegex ?? '',
        isPermanent: blockRule?.isPermanent ?? false,
        ttlHours: '',
    });
    const [errors, setErrors] = React.useState<Partial<Record<keyof BlockRuleFormValues, string>>>({});
    const [isEmailRegex, setIsEmailRegex] = React.useState(Boolean(blockRule?.emailRegex));
    const [isBinRegex, setIsBinRegex] = React.useState(Boolean(blockRule?.binRegex));
    const [regexTestValue, setRegexTestValue] = React.useState('');
    const [regexTestResult, setRegexTestResult] = React.useState<'blocked' | 'allowed' | null>(null);

    const set = (field: keyof BlockRuleFormValues, value: string | boolean) => {
        setValues((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const errs = validate(values, Boolean(blockRule?.expiresAt));
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        await onSubmit(values);
    };

    const handleTestRegex = () => {
        const pattern = values.emailRegex;
        if (!pattern) return;
        try {
            const regex = new RegExp(pattern);
            setRegexTestResult(regex.test(regexTestValue) ? 'blocked' : 'allowed');
        } catch {
            setRegexTestResult(null);
        }
    };

    return (
        <form id="block-rule-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div className="space-y-1.5">
                <Label htmlFor="name">
                    Name <span className="text-red-500">*</span>
                </Label>
                <Input
                    id="name"
                    placeholder="e.g. Block suspicious IPs"
                    value={values.name}
                    onChange={(e) => set('name', e.target.value)}
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Operator */}
            <div className="space-y-1.5">
                <Label>Operator</Label>
                <div className="mb-2 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1.5">
                    <div className="flex gap-2">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        <span>
                            <strong>OR</strong> — any one condition is enough to block
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        <span>
                            <strong>AND</strong> — all conditions must match to block
                        </span>
                    </div>
                </div>
                <Select value={values.operator} onValueChange={(v) => set('operator', v)}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="OR">OR</SelectItem>
                        <SelectItem value="AND">AND</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* IP Address */}
            <div className="space-y-1.5">
                <Label htmlFor="ipAddress">IP Address</Label>
                <div className="mb-1.5 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground flex gap-2">
                    <Info size={12} className="mt-0.5 shrink-0" />
                    <span>Supports both IPv4 and IPv6</span>
                </div>
                <Input
                    id="ipAddress"
                    placeholder="e.g. 192.168.1.1"
                    value={values.ipAddress}
                    onChange={(e) => set('ipAddress', e.target.value)}
                />
                {errors.ipAddress && <p className="text-xs text-red-500">{errors.ipAddress}</p>}
            </div>

            {/* Email / Email Regex */}
            <div className="space-y-1.5">
                {!isEmailRegex ? (
                    <>
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            placeholder="user@example.com"
                            value={values.email}
                            onChange={(e) => set('email', e.target.value)}
                        />
                        {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                    </>
                ) : (
                    <>
                        <Label htmlFor="emailRegex">Email Regex</Label>
                        <div className="mb-1.5 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground flex gap-2">
                            <Info size={12} className="mt-0.5 shrink-0" />
                            <code className="font-mono">{`^(?!.*@gmail\\.com$)[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$`}</code>
                        </div>
                        <Input
                            id="emailRegex"
                            placeholder="Regex pattern"
                            value={values.emailRegex}
                            onChange={(e) => {
                                set('emailRegex', e.target.value);
                                setRegexTestResult(null);
                            }}
                        />
                    </>
                )}
                <div className="flex items-center gap-2 mt-1">
                    <Checkbox
                        id="isEmailRegex"
                        checked={isEmailRegex}
                        onCheckedChange={(v) => {
                            setIsEmailRegex(v as boolean);
                            set('email', '');
                            set('emailRegex', '');
                            setRegexTestResult(null);
                        }}
                    />
                    <Label htmlFor="isEmailRegex" className="text-xs font-normal cursor-pointer">
                        Use regex
                    </Label>
                </div>
                {isEmailRegex && (
                    <>
                        <div className="flex gap-2 mt-1">
                            <Input
                                placeholder="Test email value"
                                value={regexTestValue}
                                onChange={(e) => {
                                    setRegexTestValue(e.target.value);
                                    setRegexTestResult(null);
                                }}
                            />
                            <Button type="button" variant="outline" size="sm" onClick={handleTestRegex}>
                                Test
                            </Button>
                        </div>
                        {regexTestResult && (
                            <p
                                className={`text-xs ${regexTestResult === 'blocked' ? 'text-red-500' : 'text-green-600'}`}
                            >
                                This value would be {regexTestResult}
                            </p>
                        )}
                    </>
                )}
            </div>

            {/* BIN / BIN Regex */}
            <div className="space-y-1.5">
                {!isBinRegex ? (
                    <>
                        <Label htmlFor="bin">BIN (6 digits)</Label>
                        <Input
                            id="bin"
                            placeholder="e.g. 453201"
                            value={values.bin}
                            onChange={(e) => set('bin', e.target.value)}
                        />
                    </>
                ) : (
                    <>
                        <Label htmlFor="binRegex">BIN Regex</Label>
                        <div className="mb-1.5 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground flex gap-2">
                            <Info size={12} className="mt-0.5 shrink-0" />
                            <span>
                                Example: <code className="font-mono">^45\d{'{4}'}$</code>
                            </span>
                        </div>
                        <Input
                            id="binRegex"
                            placeholder="Regex pattern"
                            value={values.binRegex}
                            onChange={(e) => set('binRegex', e.target.value)}
                        />
                    </>
                )}
                <div className="flex items-center gap-2 mt-1">
                    <Checkbox
                        id="isBinRegex"
                        checked={isBinRegex}
                        onCheckedChange={(v) => {
                            setIsBinRegex(v as boolean);
                            set('bin', '');
                            set('binRegex', '');
                        }}
                    />
                    <Label htmlFor="isBinRegex" className="text-xs font-normal cursor-pointer">
                        Use regex
                    </Label>
                </div>
            </div>

            {/* Permanent / TTL */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="isPermanent"
                        checked={values.isPermanent}
                        onCheckedChange={(v) => set('isPermanent', v as boolean)}
                    />
                    <Label htmlFor="isPermanent" className="cursor-pointer">
                        Permanent rule
                    </Label>
                </div>
                {!values.isPermanent && (
                    <div className="space-y-1.5">
                        <Label htmlFor="ttlHours">Expiry (hours)</Label>
                        <Input
                            id="ttlHours"
                            type="number"
                            min={1}
                            max={8760}
                            placeholder={blockRule?.expiresAt ? 'Leave blank to keep the current expiry' : 'e.g. 24'}
                            value={values.ttlHours}
                            onChange={(e) => set('ttlHours', e.target.value)}
                        />
                        {errors.ttlHours && <p className="text-xs text-red-500">{errors.ttlHours}</p>}
                    </div>
                )}
            </div>

        </form>
    );
}
